import { useState } from 'react';
import EpisodeAudioPlayer from './EpisodeAudioPlayer';
import EpisodePlayerToggle from './EpisodePlayerToggle';
import ExplicitContentBadge from './ExplicitContentBadge';

interface RelatedEpisodePodcast {
  name: string;
  url?: string | null;
  image?: string | null;
}

interface RelatedEpisode {
  id: number;
  name: string;
  url: string;
  audio_url?: string | null;
  explicit?: boolean;
  published_at?: string | null;
  starts_at_seconds?: number | null;
  discussion_summary?: string | null;
  discussion_category?: string | null;
  podcast: RelatedEpisodePodcast;
}

interface RelatedEpisodesSectionProps {
  title?: string;
  episodes: RelatedEpisode[];
  className?: string;
  viewAllUrl?: string;
  viewAllLabel?: string;
}

const formatTimestamp = (seconds?: number | null) => {
  if (seconds == null || Number.isNaN(seconds)) return null;
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  const paddedMinutes =
    hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');

  return hours > 0
    ? `${hours}:${paddedMinutes}:${paddedSeconds}`
    : `${paddedMinutes}:${paddedSeconds}`;
};

const RelatedEpisodesSection: React.FC<RelatedEpisodesSectionProps> = ({
  title = 'Related podcast episodes',
  episodes,
  className = '',
  viewAllUrl,
  viewAllLabel = 'View all episodes',
}) => {
  const [playingEpisodeId, setPlayingEpisodeId] = useState<number | null>(null);

  if (episodes.length === 0) return null;

  return (
    <div className={`container mx-auto px-4 py-8 ${className}`}>
      <div className="w-full">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-neutral">{title}</h2>
          {viewAllUrl && (
            <a
              href={viewAllUrl}
              className="text-sm font-semibold text-primary hover:text-primary/80"
            >
              {viewAllLabel}
            </a>
          )}
        </div>

        <div className="space-y-3">
          {episodes.map(episode => {
            const timestamp = formatTimestamp(episode.starts_at_seconds);
            const metaParts = [
              episode.podcast?.name,
              episode.published_at,
              timestamp ? `Starts at ${timestamp}` : null,
            ].filter(Boolean);

            return (
              <div
                key={episode.id}
                className="rounded-xl border border-base-200 bg-base-100/70 p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-center gap-3">
                  {episode.podcast?.image ? (
                    <img
                      src={episode.podcast.image}
                      alt={`${episode.podcast.name} logo`}
                      className="h-12 w-12 rounded-xl object-cover ring-1 ring-base-300/70"
                      loading="lazy"
                    />
                  ) : (
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-base-100/80 text-xs font-semibold text-base-content/70 ring-1 ring-base-300/70">
                      {episode.podcast?.name?.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0">
                    {episode.discussion_category && (
                      <span className="mb-1.5 inline-flex rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
                        {episode.discussion_category}
                      </span>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href={episode.url}
                        className="block text-base font-semibold text-base-content hover:text-blue-500"
                      >
                        {episode.name}
                      </a>
                      {episode.explicit && <ExplicitContentBadge />}
                    </div>
                    {metaParts.length > 0 && (
                      <div className="mt-1 text-sm text-base-content/70">
                        {episode.podcast?.url ? (
                          <a
                            href={episode.podcast.url}
                            className="font-medium text-base-content/80 hover:text-blue-500"
                          >
                            {episode.podcast.name}
                          </a>
                        ) : (
                          <span className="font-medium text-base-content/80">
                            {episode.podcast?.name}
                          </span>
                        )}
                        {metaParts.slice(1).length > 0 && (
                          <span> · {metaParts.slice(1).join(' · ')}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {episode.discussion_summary && (
                  <p className="mt-3 text-sm leading-relaxed text-base-content/75">
                    {episode.discussion_summary}
                  </p>
                )}
                {episode.audio_url && (
                  <>
                    <EpisodePlayerToggle
                      expanded={playingEpisodeId === episode.id}
                      playLabel={
                        episode.explicit
                          ? timestamp
                            ? `Play explicit episode from ${timestamp}`
                            : 'Play explicit episode'
                          : timestamp
                            ? `Play from ${timestamp}`
                            : 'Play episode'
                      }
                      onToggle={() =>
                        setPlayingEpisodeId(current =>
                          current === episode.id ? null : episode.id
                        )
                      }
                    />
                    {playingEpisodeId === episode.id && (
                      <EpisodeAudioPlayer
                        src={episode.audio_url}
                        title={episode.name}
                        startAtSeconds={episode.starts_at_seconds}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RelatedEpisodesSection;
